import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Calendar, CheckCircle2 } from "lucide-react";
import SEO from "../../components/SEO";
import PageHero from "../../components/common/PageHero";
import { getServiceBySlug } from "../../data/services";
import { WHATSAPP_URL } from "../../constants/social";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = service.icon;

  return (
    <div className="bg-slate-50 min-h-screen">
      <SEO
        title={`${service.title} | Wisdom Peters`}
        description={service.summary}
        name="Wisdom Peters"
        image={service.image}
        type="website"
      />

      <PageHero
        eyebrow={service.eyebrow}
        title={service.title}
        subtitle={service.summary}
        backgroundImage={service.image}
        overlayClass="bg-gradient-to-br from-black/80 via-purple-950/75 to-black/85"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: service.shortTitle },
        ]}
      />

      <div className="container mx-auto px-4 py-14 max-w-6xl -mt-2 relative z-20">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-purple-100 rounded-xl">
                  <Icon className="w-6 h-6 text-purple-700" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">About this service</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">{service.description}</p>
            </section>

            <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">What you’ll gain</h2>
              <ul className="space-y-3">
                {service.outcomes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Who it’s for</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {service.whoFor.map((item) => (
                  <li
                    key={item}
                    className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-slate-700 text-sm"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm sticky top-24">
              <h3 className="font-bold text-slate-800 mb-4">Packages</h3>
              <div className="space-y-4 mb-6">
                {service.packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="border border-slate-100 rounded-xl p-4 bg-slate-50"
                  >
                    <p className="font-semibold text-slate-800">{pkg.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{pkg.duration}</p>
                    <p className="text-purple-700 font-bold mt-2">{pkg.priceLabel}</p>
                  </div>
                ))}
              </div>

              <Link
                to={`/book-session?service=${service.slug}`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-purple-700 text-white font-semibold rounded-md hover:bg-purple-800 transition"
              >
                <Calendar className="w-4 h-4" />
                Book available date
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-green-600 text-green-700 font-semibold rounded-md hover:bg-green-50 transition"
              >
                Enquire on WhatsApp
              </a>
              <Link
                to={`/contact?interest=${service.slug}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1 text-sm font-medium text-purple-700 hover:underline"
              >
                Or send a detailed enquiry <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                After you choose a date, you’ll confirm details and complete
                payment securely via Stripe invoice or checkout link.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
